import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import { IconButton } from '@/shared/ui/IconButton/IconButton';
import CloseMIcon from '@/shared/assets/icons/close-m.svg';
import s from './BottomSheet.module.scss';

export interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  className?: string;
  contentClassName?: string;
  headerClassName?: string;
  hideHeader?: boolean;
}

export const BottomSheet: FC<BottomSheetProps> = ({
  open,
  onClose,
  title,
  children,
  className,
  contentClassName,
  headerClassName,
  hideHeader = false,
}) => {
  const [mounted, setMounted] = useState(open);
  const [animOpen, setAnimOpen] = useState(false);
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startYRef = useRef<number | null>(null);
  const overflowRef = useRef<string>('');

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (open) {
      setMounted(true);
      overflowRef.current = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      timer = setTimeout(() => setAnimOpen(true), 10);
    } else {
      setAnimOpen(false);
      setDragY(0);
      setIsDragging(false);
      timer = setTimeout(() => {
        setMounted(false);
        document.body.style.overflow = overflowRef.current;
      }, 300);
    }
    return () => {
      clearTimeout(timer);
      document.body.style.overflow = overflowRef.current;
    };
  }, [open]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleDragStart = (e: React.TouchEvent | React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button, input')) return;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    startYRef.current = clientY;
    setIsDragging(true);
  };

  const handleDragMove = (e: React.TouchEvent) => {
    if (startYRef.current === null || !isDragging) return;
    const clientY = e.touches[0].clientY;
    const delta = clientY - startYRef.current;
    if (delta > 0) {
      setDragY(delta);
    } else {
      setDragY(delta * 0.15);
    }
  };

  const handleDragEnd = () => {
    if (startYRef.current === null) return;
    startYRef.current = null;
    setIsDragging(false);
    setDragY((prev) => {
      if (prev > 80) {
        handleClose();
      }
      return 0;
    });
  };

  useEffect(() => {
    if (!isDragging) return;

    const onWindowMouseMove = (e: MouseEvent) => {
      if (startYRef.current === null) return;
      const delta = e.clientY - startYRef.current;
      if (delta > 0) {
        setDragY(delta);
      } else {
        setDragY(delta * 0.15);
      }
    };

    const onWindowMouseUp = () => {
      if (startYRef.current === null) return;
      startYRef.current = null;
      setIsDragging(false);
      setDragY((prev) => {
        if (prev > 80) {
          handleClose();
        }
        return 0;
      });
    };

    window.addEventListener('mousemove', onWindowMouseMove);
    window.addEventListener('mouseup', onWindowMouseUp);
    return () => {
      window.removeEventListener('mousemove', onWindowMouseMove);
      window.removeEventListener('mouseup', onWindowMouseUp);
    };
  }, [isDragging, handleClose]);

  if (!mounted && !open) return null;

  return createPortal(
    <div className={s.overlay}>
      <div className={classNames(s.backdrop, { [s.animOpen]: animOpen })} onClick={handleClose} />
      <div
        className={classNames(s.sheetContainer, { [s.animOpen]: animOpen })}
        style={
          animOpen && (dragY !== 0 || isDragging)
            ? {
                transform: `translateY(${dragY}px)`,
                transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
              }
            : undefined
        }
      >
        <div
          className={s.handleArea}
          onTouchStart={handleDragStart}
          onTouchMove={handleDragMove}
          onTouchEnd={handleDragEnd}
          onMouseDown={handleDragStart}
        >
          <div className={s.handle} />
        </div>
        <div className={classNames(s.sheet, className)}>
          {!hideHeader && (
            <>
              <div
                className={classNames(s.header, headerClassName)}
                onTouchStart={handleDragStart}
                onTouchMove={handleDragMove}
                onTouchEnd={handleDragEnd}
                onMouseDown={handleDragStart}
              >
                <span className={s.title}>{title}</span>
                <IconButton size="s" className={s.closeBtn} onClick={handleClose}>
                  <CloseMIcon className={s.closeIcon} />
                </IconButton>
              </div>
              <div className={s.divider} />
            </>
          )}
          <div className={classNames(s.content, contentClassName)}>{children}</div>
        </div>
      </div>
    </div>,
    document.body,
  );
};
